<template>
    <div><slot></slot></div>
</template>
<script>

import Store from '@/stores/store.js'
import Bus from '@/bus/BusEvent.js'
import Raf from '@/utils/raf.js'
import Debounce from '@/utils/Debounce.js'
import GL from '@/views/GL/GL.js'

export default {
    name: 'TextFontGroup',

    props: {
        top: { type: Number, default: 0 },
        left: { type: Number, default: 0 },
        x: { type: Number, default: 0 },
        y: { type: Number, default: 0 },
        url: String,
        isSelected: { type: Boolean, default: false},
        currentState: { type: Number, default: 0 },
        nameCursor: { type: String, default: '' },
        isResizing: { type: Boolean, default: false },
        active: { type: Boolean, default: true },
        enter: {type: Function},
        leave: {type: Function},
        scaleForce: { type: Number, default: 20 },
        scaleY:  { type: Number, default: 1 },
        name: {type: String, default: ''}
    },
    
    data() {
        return {
            isHovered: false,
            isClicked: null,
            height: 0,
            width: 0,
            hoverPlane: null,
            state: Store.state,
            transition: this.x,
            scale: this.scaleForce,
            hoverScale: 1,
            debug: window.location.hash === '#debug',
        }
    },

    created(){
        this.group = new THREE.Group();
        this.group.mouseEnter = this.mouseEnter
        this.group.mouseLeave = this.mouseLeave
        GL.addHoverableGroup(this.group);
        this._onClick = this.onClick.bind(this);
        Bus.$on('click', this._onClick);
        this.resizeSmoothDebounced = Debounce(this.resize, 500);

        this.intervalResizer(2500, this.updatePlaneSize)
        
        Bus.$on('resize', () => {
            this.resizeSmoothDebounced();
        })

        window.addEventListener('touchstart', this.touchHandlerStart)
        window.addEventListener('touchmove', this.touchHandler)
        window.addEventListener('touchend', this.touchHandlerOff)
    },

    methods: {
        setPosition(){
            this.group.position.x = this.left + this.transition;
            this.group.position.y = this.top;
        },

        setScale() {
            this.group.scale.set( this.scale, this.scaleY, 1 );
        },

        resetScale() {
            this.scale = this.scaleForce
        },


        setScaleY(scale) {
            this.group.scale.set( this.scale, scale, 1 );
        },

        enterFontTransition(duration) {            
            if(this.group.children.length > 2) this.$children[1].enterFontTransition(duration)
            else this.$children[0].enterFontTransition(duration)
        },

        leaveFontTransition(duration) {            
            if(this.group.children.length > 2) this.$children[1].leaveFontTransition(duration)
            else this.$children[0].leaveFontTransition(duration)
        },

        setHoverPlaneSize() {
            let heightBox = new THREE.Box3().setFromObject(this.group);
            this.height = heightBox.getSize(new THREE.Vector3()).y
            this.width = heightBox.getSize(new THREE.Vector3()).x
        },
        setHoverPlane() {
            /**
             * Sets plane that helps detect raycasting on text
             */
            this.setHoverPlaneSize()
            var geometry = new THREE.PlaneGeometry(this.width,  this.height, 1, 1);
            var material = new THREE.MeshBasicMaterial( {color: this.url.includes('/directors') ? 0xffff00 : 0xff0000, side: THREE.DoubleSide} );
            this.hoverPlane = new THREE.Mesh( geometry, material );
            if(!this.debug) this.hoverPlane.visible = false 

            this.setHoverPlanePosition()
            this.group.add( this.hoverPlane );
        },
        setHoverPlanePosition() {
            if(this.group.children[1]) this.hoverPlane.position.y =  -this.height/2 + this.group.children[1].position.y
            else this.hoverPlane.position.y =  -this.height/2 + this.group.children[0].position.y
            this.hoverPlane.position.x = this.width/2
        },
        touchHandlerStart(){
            this.isClicked = new Date()
        },
        touchHandler(_e) {
            /**
             * Scale effect on scroll list for mobile
             */
            if(!Store.isDesktop && this.$route.name === this.name ) {
                let offset = (this.name === 'news') ? 150 : 75;
                let threshold = 0.1
                let posGroup = (this.top + offset ) / (Store.scrollHeight)
                let ratioScroll = Store.scrollPosition / Store.scrollDistance
                if( posGroup > (ratioScroll - threshold ) && posGroup < (ratioScroll + threshold))  {
                    TweenMax.to(this, 1, {hoverScale: 1.2, ease: Power3.easeOut, onUpdate: this.handleHover
                        });
                }
                else {
                    TweenMax.to(this, 1, {hoverScale: 1, ease: Power3.easeOut, onUpdate: this.handleHover
                    });
                }
            }
        },

        handleHover() {
            this.group.scale.set( this.hoverScale, 1, 1 );
        },

        touchHandlerOff() {
            TweenMax.to(this, 1, {hoverScale: 1, ease: Power3.easeOut, onUpdate: this.handleHover
            });                
            
            /**
            * For click on ios, if time between touchstart & end is less than 50ms than click
            */

            if(new Date() - this.isClicked < 50) this.onClick()
        },

        updatePlane() {
            this.hoverPlane.scale.set( 1, this.getBoundingHeight()/(this.height), 1 );
            let heightBox = new THREE.Box3().setFromObject(this.group);
            this.height = this.getBoundingHeight()

            
            if(this.group.children.length > 2)
            this.hoverPlane.position.y =  -this.height/2 + this.group.children[1].position.y
            else 
            this.hoverPlane.position.y =  -this.height/2 + this.group.children[0].position.y + 10
        },

        updatePlaneSize() {
            let el 
            if(this.group.children.length > 2)
            el = this.group.children[1]
            else 
            el = this.group.children[0]

            el.geometry.computeBoundingBox()
            let heightBox = new THREE.Box3().setFromObject(el);
            if(heightBox.getSize(new THREE.Vector3()).x+1 < this.width) {
                this.hoverPlane.scale.set( el.geometry.boundingBox.max.x/this.width, 1, 1 );
                this.hoverPlane.position.x = el.geometry.boundingBox.max.x/2
            }
            else {
                this.hoverPlane.scale.set( 1, 1, 1 );
                this.hoverPlane.position.x = this.width/2
            }
            
        },

        intervalResizer(timeout, fn, clear = true) {
            /**
             * Help resizer event 
             */
            let interval = setInterval(() => {
                fn()
            }, 200);

            setTimeout(() => {
                if(clear) clearInterval(interval)
            }, timeout);
        },
        getBoundingHeight() {
            /**
             * Helper to get height of group WITHOUT plane inside, else it doest register 
             */
            if(this.group.children.length > 2) {
                let textup = new THREE.Box3().setFromObject(this.group.children[0]);
                let textdown = new THREE.Box3().setFromObject(this.group.children[1]);
                return textdown.getSize(new THREE.Vector3()).y + textup.getSize(new THREE.Vector3()).y
            }
            else {
                let textup = new THREE.Box3().setFromObject(this.group.children[0]);
                return textup.getSize(new THREE.Vector3()).y 
            }
        },

        setChildrenPosition() {
            if (this.group.children.length > 1 ) {
                let textdown = new THREE.Box3().setFromObject(this.group.children[1]);
                this.group.children[1].position.y = textdown.getSize(new THREE.Vector3()).y + this.$children[1].top
            }
        },

        updateChildrenPosition() {
            if (this.group.children.length > 2 ) {
                let textdown = new THREE.Box3().setFromObject(this.group.children[1]);
                this.group.children[1].position.y = textdown.getSize(new THREE.Vector3()).y + this.$children[1].top
            }
        },

        resize() {
            this.updateChildrenPosition()
            this.intervalResizer(1000, this.updatePlane)
        },

        setSelected() {
            this.$children.forEach(child => {
                child.changeSelected(this.isSelected)
            });
        },

        updateFontsTransition(){
            if(Store.isDesktop) {
                this.$children.forEach(child => {
                    child.setState(this.currentState)
                });
            }
        },

        initFontsTransition(){
            if(Store.isDesktop) {
                this.$children.forEach(child => {
                    child.updateFontTransition(true)
                });
            }
        },

        mouseEnter(){
            this.state.cursorText = this.cursorName();
            this.isHovered = true;
            for (let index = 0; index < this.$children.length; index++) {
                this.$children[index].isActive = this.active
                this.$children[index].textGL.mouseEnter(this.active)
            }
        },
        mouseMove() {
            this.state.cursorText = this.cursorName();
        },
        mouseLeave(){ 
            this.isHovered = false;
            for (let index = 0; index < this.$children.length; index++) {
                this.$children[index].textGL.mouseLeave()
            }
        },
        cursorName(){
            this.isSelected ? this.state.cursorHover = false : this.state.cursorHover = true
            return this.isSelected ? 'Back' : this.nameCursor;
        },

        onClick(){
            if(Store.isTouch) {
                setTimeout(() => {
                    if( !!this.url && this.isHovered && this.url !== this.$route.path && !Store.state.cursorLocked){
                        this.$router.push( this.url );
                    }
                }, 20);
            }
            else {
                if( !!this.url && this.isHovered && this.url !== this.$route.path && !Store.state.cursorLocked){
                    this.$router.push( this.url );
                }
            }

        },

        onEnter(index, fn, immediate) {
            this.enter(index, this, immediate)
        },

        onLeave(index) {
            this.leave(index, this)
        },

        init() {
            for (let index = 0; index < this.$children.length; index++) {
                this.group.add(this.$children[index].textGL.mesh);
            }
            
            if(this.state.level !== 0) {
                this.initFontsTransition()
                setTimeout(() => {
                    this.initFontsTransition()
                    this.updateFontsTransition()
                }, 200);
            }

            this.setSelected();
            this.setChildrenPosition()
            this.setPosition();
            this.setHoverPlane();
        },
    },

    mounted(){
        this.init();
    },

    watch: {
        top: function(){
            this.setPosition()
        },
        left: function(){
            this.setPosition()
        },
        isSelected:  function(){
            this.setSelected()
        },
        currentState: function(newVal, oldVal){
            this.updateFontsTransition();
            if(this.isResizing) this.intervalResizer(1500, this.updatePlaneSize)
        },
    }
}
</script>